import React from "react";
import PropTypes from "prop-types";
import { Pagination, PaginationItem, PaginationLink, Input } from "reactstrap";

/**
 * Compact pagination + page-size selector for card grids.
 *
 * Props:
 * - page: current 1-based page
 * - pageSize: items per page
 * - total: total filtered items
 * - onPageChange(p)
 * - onPageSizeChange(n)
 * - pageSizeOptions: array of page-size choices
 */
const CardPagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24, 48],
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  if (total === 0) return null;

  // Compute a windowed page list — show 1, current ± 1, last with "…" gaps
  const buildPageList = () => {
    const pages = new Set([1, totalPages, safePage - 1, safePage, safePage + 1]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  };

  const visible = buildPageList();
  const items = [];
  visible.forEach((p, i) => {
    if (i > 0 && p - visible[i - 1] > 1) {
      items.push({ ellipsis: true, key: `e-${p}` });
    }
    items.push({ page: p, key: p });
  });

  const start = (safePage - 1) * pageSize + 1;
  const end = Math.min(total, safePage * pageSize);

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between mt-4 gap-2">
      <div className="d-flex align-items-center gap-2">
        <small className="text-muted mb-0">
          Affichage <strong>{start}</strong>–<strong>{end}</strong> sur{" "}
          <strong>{total}</strong>
        </small>
        <div className="d-flex align-items-center ms-3">
          <small className="text-muted me-2">Par page</small>
          <Input
            type="select"
            bsSize="sm"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            style={{ width: 80 }}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Input>
        </div>
      </div>

      <Pagination size="sm" className="mb-0 pagination-rounded">
        <PaginationItem disabled={safePage <= 1}>
          <PaginationLink first onClick={() => onPageChange(1)} />
        </PaginationItem>
        <PaginationItem disabled={safePage <= 1}>
          <PaginationLink previous onClick={() => onPageChange(safePage - 1)} />
        </PaginationItem>
        {items.map((it) =>
          it.ellipsis ? (
            <PaginationItem key={it.key} disabled>
              <PaginationLink>…</PaginationLink>
            </PaginationItem>
          ) : (
            <PaginationItem key={it.key} active={it.page === safePage}>
              <PaginationLink onClick={() => onPageChange(it.page)}>
                {it.page}
              </PaginationLink>
            </PaginationItem>
          )
        )}
        <PaginationItem disabled={safePage >= totalPages}>
          <PaginationLink next onClick={() => onPageChange(safePage + 1)} />
        </PaginationItem>
        <PaginationItem disabled={safePage >= totalPages}>
          <PaginationLink last onClick={() => onPageChange(totalPages)} />
        </PaginationItem>
      </Pagination>
    </div>
  );
};

CardPagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
};

export default CardPagination;
